import React, { useContext, useState, useCallback, useMemo } from 'react';
import { Loader2Icon, CheckCircle2Icon, HelpCircleIcon, MessageSquareIcon, AlertTriangleIcon } from 'lucide-react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Input } from '../ui/input';
import { ToolCallHandlersContext } from './chart-tool-call';
import { showInConciseMode } from '../chat/threadSettings';
import { cn } from '@/lib/utils';

interface ChoiceToolCallProps {
  toolName: string;
  toolCallId: string;
  args: any;
  argsText: string;
  result?: string;
  status?: { type: 'running' | 'complete' | 'incomplete' | 'interrupted'; reason?: string };
}

interface ChoiceOption {
  label: string;
  description?: string | null;
}

interface ChoiceQuestion {
  header: string;
  question: string;
  options: ChoiceOption[];
  multi_select?: boolean;
}

interface SubmittedAnswer {
  header: string;
  question: string;
  selected: string[];
}

const OTHER_VALUE = '__other__';

function safeParse(value: unknown): any {
  if (!value) return null;
  if (typeof value === 'object') return value;
  try {
    return JSON.parse(value as string);
  } catch {
    return null;
  }
}

/**
 * Questions can arrive on the interrupt value or on the raw tool args.
 *
 * Prefer the interrupt value: the tool strips the model's self-authored
 * "Other"/"Something else" options before interrupting, and `args` is the unfiltered
 * model output. Reading args first would render exactly the duplicates the server just
 * removed. Args remain the fallback so the question still shows while streaming, before
 * the interrupt value lands.
 */
function extractQuestions(args: any, argsText: string, result?: string): ChoiceQuestion[] {
  const fromResult = safeParse(result);
  if (Array.isArray(fromResult?.questions) && fromResult.questions.length > 0) {
    return fromResult.questions;
  }
  const fromArgs = typeof args === 'object' && args ? args : safeParse(argsText);
  if (Array.isArray(fromArgs?.questions)) {
    return fromArgs.questions;
  }
  return [];
}

export const ChoiceToolCall: React.FC<ChoiceToolCallProps> = ({
  args,
  argsText,
  result,
  status,
}) => {
  const handlers = useContext(ToolCallHandlersContext);

  // selections[i] = chosen labels for question i (single-select keeps one entry)
  const [selections, setSelections] = useState<Record<number, string[]>>({});
  const [otherText, setOtherText] = useState<Record<number, string>>({});
  const [submitted, setSubmitted] = useState<SubmittedAnswer[] | null>(null);

  const questions = useMemo(
    () => extractQuestions(args, argsText, result),
    [args, argsText, result]
  );

  const parsedResult = useMemo(() => safeParse(result), [result]);
  const isAwaiting = parsedResult?.status === 'awaiting_choice' || status?.type === 'interrupted';

  // Answers persisted on the tool result, so a remount or page reload still renders
  // the answered state rather than an empty picker.
  const answeredFromResult: SubmittedAnswer[] | null = useMemo(() => {
    if (parsedResult?.status !== 'answered') return null;
    return Array.isArray(parsedResult?.answers) ? parsedResult.answers : null;
  }, [parsedResult]);

  // The user ignored the picker and typed instead. Their words are their own message
  // in the transcript, so this card only needs to stop claiming an answer.
  const repliedInChat = parsedResult?.status === 'replied_in_chat';

  const resolveSelected = useCallback(
    (qIndex: number): string[] =>
      (selections[qIndex] || []).map(v =>
        v === OTHER_VALUE ? (otherText[qIndex] || '').trim() : v
      ).filter(Boolean),
    [selections, otherText]
  );

  const submit = useCallback(
    (override?: { qIndex: number; selected: string[] }) => {
      const answers: SubmittedAnswer[] = questions.map((q, i) => ({
        header: q.header,
        question: q.question,
        selected: override && override.qIndex === i ? override.selected : resolveSelected(i),
      }));
      setSubmitted(answers);
      handlers?.onResumeGraph?.({ status: 'answered', answers });
    },
    [questions, resolveSelected, handlers]
  );

  // Every question answered? Gates the submit button.
  const allAnswered = questions.length > 0 && questions.every((_, i) => resolveSelected(i).length > 0);

  // A lone single-select question needs no confirmation — the click IS the answer.
  // With several questions, one click can't confirm the others, so always confirm.
  const autoSubmitOnPick = questions.length === 1 && !questions[0]?.multi_select;

  const handleRadioPick = useCallback(
    (qIndex: number, value: string) => {
      setSelections(prev => ({ ...prev, [qIndex]: [value] }));
      if (autoSubmitOnPick && value !== OTHER_VALUE) {
        submit({ qIndex, selected: [value] });
      }
    },
    [autoSubmitOnPick, submit]
  );

  const toggleCheckbox = useCallback((qIndex: number, value: string, checked: boolean) => {
    setSelections(prev => {
      const current = prev[qIndex] || [];
      return {
        ...prev,
        [qIndex]: checked ? [...current, value] : current.filter(v => v !== value),
      };
    });
  }, []);

  // ── Skipped: answered in the chat instead of the picker ──
  if (repliedInChat && !submitted) {
    const question = questions[0]?.question;
    return (
      <Card>
        <div className="p-3">
          <div className="flex items-start gap-2">
            <MessageSquareIcon className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" />
            <p className="text-sm m-0 text-muted-foreground">
              {question ? <>{question} — <span className="italic">answered in chat</span></> : 'Answered in chat'}
            </p>
          </div>
        </div>
      </Card>
    );
  }

  // ── Answered ──
  const answers = submitted || answeredFromResult;
  if (answers) {
    return (
      <Card>
        <div className="p-3 space-y-2">
          {answers.map((a, i) => (
            <div key={i} className="flex items-start gap-2">
              <CheckCircle2Icon className="h-4 w-4 text-emerald-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm m-0 text-foreground">
                {a.question ? (
                  <span className="text-muted-foreground">{a.question} </span>
                ) : null}
                <strong>{a.selected.join(', ')}</strong>
              </p>
            </div>
          ))}
        </div>
      </Card>
    );
  }

  // ── Awaiting an answer — render the picker ──
  if (isAwaiting && questions.length > 0) {
    return (
      <Card>
        <div className="p-3 space-y-4">
          {questions.map((q, qIndex) => {
            const selected = selections[qIndex] || [];
            const isMulti = !!q.multi_select;
            return (
              <div key={qIndex} className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] uppercase tracking-wide font-medium text-muted-foreground bg-muted rounded px-1.5 py-0.5">
                    {q.header}
                  </span>
                  {isMulti && (
                    <span className="text-[10px] text-muted-foreground">Select all that apply</span>
                  )}
                </div>
                <p className="text-sm m-0 text-foreground font-medium">{q.question}</p>

                {isMulti ? (
                  <div className="grid gap-1">
                    {[...q.options, { label: OTHER_VALUE }].map((opt, oIndex) => {
                      const isOther = opt.label === OTHER_VALUE;
                      const checked = selected.includes(opt.label);
                      return (
                        <label
                          key={oIndex}
                          className={cn(
                            'flex items-start gap-2 rounded-md border border-transparent px-2 py-1.5 cursor-pointer hover:bg-muted/60',
                            checked && 'bg-muted/60 border-border'
                          )}
                        >
                          <Checkbox
                            className="mt-0.5"
                            checked={checked}
                            onCheckedChange={c => toggleCheckbox(qIndex, opt.label, !!c)}
                          />
                          <span className="text-sm leading-tight">
                            {isOther ? (
                              <span className="text-muted-foreground">Other</span>
                            ) : (
                              <>
                                {opt.label}
                                {opt.description && (
                                  <span className="block text-xs text-muted-foreground">
                                    {opt.description}
                                  </span>
                                )}
                              </>
                            )}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                ) : (
                  <RadioGroup
                    value={selected[0] || ''}
                    onValueChange={v => handleRadioPick(qIndex, v)}
                    className="gap-1"
                  >
                    {[...q.options, { label: OTHER_VALUE }].map((opt, oIndex) => {
                      const isOther = opt.label === OTHER_VALUE;
                      const checked = selected[0] === opt.label;
                      return (
                        <label
                          key={oIndex}
                          className={cn(
                            'flex items-start gap-2 rounded-md border border-transparent px-2 py-1.5 cursor-pointer hover:bg-muted/60',
                            checked && 'bg-muted/60 border-border'
                          )}
                        >
                          <RadioGroupItem className="mt-0.5" value={opt.label} />
                          <span className="text-sm leading-tight">
                            {isOther ? (
                              <span className="text-muted-foreground">Other</span>
                            ) : (
                              <>
                                {opt.label}
                                {opt.description && (
                                  <span className="block text-xs text-muted-foreground">
                                    {opt.description}
                                  </span>
                                )}
                              </>
                            )}
                          </span>
                        </label>
                      );
                    })}
                  </RadioGroup>
                )}

                {selected.includes(OTHER_VALUE) && (
                  <Input
                    autoFocus
                    placeholder="Type your answer…"
                    value={otherText[qIndex] || ''}
                    onChange={e => setOtherText(prev => ({ ...prev, [qIndex]: e.target.value }))}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && allAnswered) {
                        e.preventDefault();
                        submit();
                      }
                    }}
                    className="h-8 text-sm"
                  />
                )}
              </div>
            );
          })}

          {!autoSubmitOnPick && (
            <Button size="sm" disabled={!allAnswered} onClick={() => submit()}>
              Submit
            </Button>
          )}
          {autoSubmitOnPick && selections[0]?.includes(OTHER_VALUE) && (
            <Button size="sm" disabled={!allAnswered} onClick={() => submit()}>
              Submit
            </Button>
          )}

          <p className="text-xs text-muted-foreground m-0">
            Or just type your answer in the chat below.
          </p>
        </div>
      </Card>
    );
  }

  // ── Still streaming the question ──
  if (status?.type === 'running') {
    return (
      <Card>
        <div className="p-3">
          <div className="flex items-center gap-2">
            <Loader2Icon className="h-4 w-4 text-primary animate-spin flex-shrink-0" />
            <p className="text-sm m-0 text-muted-foreground">Preparing a question…</p>
          </div>
        </div>
      </Card>
    );
  }

  // The question never came through — Ellie's call failed validation, was cut off, or the
  // step ended before it finished. Say so instead of leaving an empty "Question" box.
  const statusType = (status as { type?: string } | undefined)?.type;
  const failed =
    statusType === 'error' ||
    statusType === 'incomplete' ||
    (typeof result === 'string' && /^(Error|Stopped|This step didn't complete|Not run)/.test(result));
  if (failed) {
    return (
      <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 dark:border-amber-700 dark:bg-amber-950/30">
        <AlertTriangleIcon className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="space-y-0.5">
          <p className="text-sm m-0 font-medium text-foreground">Ellie's question didn't come through</p>
          <p className="text-sm m-0 text-muted-foreground">
            Type your answer in the chat below, or ask her to try again.
          </p>
        </div>
      </div>
    );
  }

  // Fallback (e.g. the question was cancelled)
  return (
    <Card>
      <div className="p-3">
        <div className="flex items-center gap-2">
          <HelpCircleIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          <p className="text-sm m-0 text-muted-foreground">Question</p>
        </div>
      </div>
    </Card>
  );
};

// Interactive (interrupt/resume) — must stay visible in concise mode so the user
// can actually answer.
showInConciseMode(ChoiceToolCall);
